// GrowthChart.js
import React, { useState } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { Paper, Typography, Box, FormControlLabel, Checkbox, Button } from '@mui/material';
import { Divider } from 'antd';

const lineMap = {
  p01: '0.1%',
  p1: '1%',
  p3: '3%',
  p5: '5%',
  p10: '10%',
  p15: '15%',
  p25: '25%',
  p50: '50%',
  patient: 'Paciente',
  p75: '75%',
  p85: '85%',
  p90: '90%',
  p95: '95%',
  p97: '97%',
  p99: '99%',
};

const colorMap = {
  p01: '#cfd8dc',
  p1: '#b0bec5',
  p3: '#90a4ae',
  p5: '#78909c',
  p10: '#607d8b',
  p15: '#546e7a',
  p25: '#455a64',
  p50: '#000000',
  patient: '#2740b0ff',
  p75: '#4caf50',
  p85: '#ffc107',
  p90: '#ff9800',
  p95: '#ff5722',
  p97: '#f44336',
  p99: '#e91e63',
};

const labelCustom = (key) => lineMap[key] || key;
const disableChecks = ['p01', 'p1', 'p5', 'p15', 'p85', 'p95', 'p99'];

const GrowthChart = ({ data }) => {
  const [showControls, setShowControls] = useState(false);
  const [visibleLines, setVisibleLines] = useState(() =>
    Object.keys(lineMap).reduce((acc, key) => {
      acc[key] = disableChecks.includes(key) ? false : true;
      return acc;
    }, {})
  );

  // Cambia visibilidad al hacer click en el checkbox
  const toggleLine = (key) => {
    setVisibleLines((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const dataKeXAxis = (data) => {
    switch (data.name) {
      case 'HEAD_CIRCUMFERENCE_FOR_AGE':
      case 'WEIGHT_FOR_AGE':
      case 'LENGTH_HEIGHT_FOR_AGE':
      case 'BODY_MAX_INDEX_FOR_AGE':
        return 'period';
      case 'WEIGHT_FOR_LENGTH_HEIGHT':
        return data.range === '2,5' ? 'height' : 'length';
    }
  };

  const labelKeXAxis = (data) => {
    switch (data.name) {
      case 'LENGTH_HEIGHT_FOR_AGE':
        return 'Semana';
      case 'WEIGHT_FOR_LENGTH_HEIGHT':
        return data.range === '2,5' ? 'Altura (cm)' : 'Peso (kg)';
      case 'BODY_MAX_INDEX_FOR_AGE':
      case 'HEAD_CIRCUMFERENCE_FOR_AGE':
      case 'WEIGHT_FOR_AGE':
        return 'Meses';
    }
  };

  const labelKeYAxis = (name) => {
    switch (name) {
      case 'LENGTH_HEIGHT_FOR_AGE':
        return 'Talla (cm)';
      case 'WEIGHT_FOR_LENGTH_HEIGHT':
        return 'Peso (kg)';
      case 'WEIGHT_FOR_AGE':
        return 'Peso (kg)';
      case 'HEAD_CIRCUMFERENCE_FOR_AGE':
        return 'Perímetro Cefálico (cm)';
      case 'BODY_MAX_INDEX_FOR_AGE':
        return "IMC KG'm2";
    }
  };

  const domainRange = () =>{
    switch (data.name) {
      case 'LENGTH_HEIGHT_FOR_AGE':
        return data.range === '2,5' ? [74,125] : [43,96];
      case 'WEIGHT_FOR_LENGTH_HEIGHT':
        return data.range === '2,5' ? [5,32] : [0,25];
      case 'WEIGHT_FOR_AGE':
        return [2,25];
      case 'HEAD_CIRCUMFERENCE_FOR_AGE':
        return [30,56];
      case 'BODY_MAX_INDEX_FOR_AGE':
        return [10,22];
    }
  };

  return (
    <Paper elevation={3} sx={{ padding: 3, marginTop: 4 }}>
      <Typography variant="h6" gutterBottom>
        {data.description}
      </Typography>
      {/* SECCION DE CONTROLES */}
      <Button variant="outlined" size="small" onClick={(e)=>setShowControls(!showControls)}
        sx={{
        color: 'rgb(20, 143, 159)',
        borderColor: 'rgb(20, 143, 159)',
        '&:hover': {
          borderColor: 'rgb(20, 143, 159)',
          color: 'rgb(20, 143, 159)',
        },
      }}>
        { showControls ? 'Cerrar' : 'Modificar Gráfico' }
      </Button>
      {
        showControls ? (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,            // espacio reducido
              rowGap: 0.5,       // aún menos espacio vertical
              columnGap: 1.5,    // espacio horizontal moderado
              mb: 2
            }}
          >
            {Object.entries(lineMap).map(([key, label]) => (
              <FormControlLabel
                key={key}
                control={
                  <Checkbox
                    checked={visibleLines[key]}
                    onChange={() => toggleLine(key)}
                    size="small"
                    sx={{
                      padding: '2px', // ya lo tenías
                      color: 'rgb(20, 143, 159)', // color cuando NO está chequeado
                      '&.Mui-checked': {
                        color: 'rgb(20, 143, 159)', // color cuando está chequeado
                      },
                    }}
                  />
                }
                label={
                  <Typography variant="caption" sx={{ fontSize: 11 }}>
                    {label}
                  </Typography>
                }
                sx={{ marginRight: 1, marginBottom: 0.5 }}
              />
            ))}
          </Box>
        ) : null
      }
      <Divider />
      {/* SECCION DE GRAFICO */}
      <Box sx={{ height: 400 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data.standard_indicators}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey={dataKeXAxis(data)} label={{ value: labelKeXAxis(data), position: 'insideBottomRight', offset: -5 }} />
            <YAxis
              label={{ value: labelKeYAxis(data.name), angle: -90, position: 'insideLeft' }}
              type="number"
              allowDecimals={false}
              domain={domainRange(data)}
              tickCount={6}
              />
            <Tooltip
              formatter={(value, name) => {
                return [value, lineMap[name] || name];
              }}
            />
            <Legend
              verticalAlign="top"
              height={36}
              content={({ payload }) => (
                <ul style={{ display: 'flex', flexWrap: 'wrap', listStyle: 'none', padding: 0 }}>
                  {payload.map((entry, index) => (
                    <li key={`item-${index}`} style={{ marginRight: 16, display: 'flex', alignItems: 'center' }}>
                      <div style={{
                        width: 12,
                        height: 12,
                        backgroundColor: entry.color,
                        marginRight: 6
                      }} />
                      <span style={{ fontSize: 12, color: '#455a64' }}>
                        {labelCustom(entry.value)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            />
            {Object.keys(lineMap).map(
              (key) =>
                visibleLines[key] && (
                  <Line
                    key={key}
                    type="monotone"
                    dataKey={key}
                    stroke={colorMap[key] || '#000'}
                    dot={key === 'patient'}
                    strokeWidth={key === 'patient' ? 3 : 1.5}
                  />
                )
            )}
          </LineChart>
        </ResponsiveContainer>
      </Box>
    </Paper>
  );
};

export default GrowthChart;
